'use client';

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Zap, TrendingUp, AlertTriangle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const PLAN_LIMITS: Record<string, number | null> = {
  free: 25_000,
  pro: 500_000,
  team: 2_000_000,
  enterprise: null,
};

const PLAN_DISPLAY: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  team: 'Team',
  enterprise: 'Enterprise',
};

interface UsageData {
  used: number;
  limit: number;
}

interface BillingData {
  plan: string;
}

export default function UsagePage(): import('react/jsx-runtime').JSX.Element {
  const { data: usage, isLoading: usageLoading } = useQuery<UsageData>({
    queryKey: ['usage'],
    queryFn: (): Promise<UsageData> =>
      fetch('/api/orgs/active/usage').then(
        (r): Promise<UsageData> => r.json() as Promise<UsageData>,
      ),
    staleTime: 60_000,
  });

  const { data: billing, isLoading: billingLoading } = useQuery<BillingData>({
    queryKey: ['billing-subscription'],
    queryFn: (): Promise<BillingData> =>
      fetch('/api/billing/subscription').then(
        (r): Promise<BillingData> => r.json() as Promise<BillingData>,
      ),
    staleTime: 5 * 60_000,
  });

  const isLoading = usageLoading || billingLoading;

  const plan = billing?.plan ?? 'free';
  const planLabel = PLAN_DISPLAY[plan] ?? plan;
  const planLimit = PLAN_LIMITS[plan] ?? 25_000;
  const isCustom = planLimit === null;

  const used = usage?.used ?? 0;
  // Use server-provided limit if available; fall back to plan map.
  const limit = usage?.limit ?? planLimit ?? 25_000;
  const percentage = isCustom ? 0 : Math.min(100, (used / limit) * 100);
  const remaining = isCustom ? null : Math.max(0, limit - used);

  const atLimit = !isCustom && percentage >= 100;
  const nearLimit = !isCustom && !atLimit && percentage >= 80;

  let barColor = 'bg-primary';
  if (atLimit) barColor = 'bg-destructive';
  else if (nearLimit) barColor = 'bg-yellow-500';

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold">Usage & Analytics</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Current billing cycle execution usage for your organization.
        </p>
      </div>

      {/* Current cycle card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              Executions this cycle
            </CardTitle>
            <Badge variant={atLimit ? 'destructive' : nearLimit ? 'secondary' : 'outline'}>
              {isLoading ? '…' : planLabel}
            </Badge>
          </div>
          {(atLimit || nearLimit) && (
            <CardDescription className="flex items-center gap-1.5 text-xs mt-1">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {atLimit
                ? 'Execution limit reached. New executions will be rejected until you upgrade or the cycle resets.'
                : 'Approaching your execution limit. Consider upgrading to avoid disruption.'}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Progress bar */}
          <div className="space-y-1.5">
            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  barColor,
                  isLoading && 'opacity-30 animate-pulse',
                )}
                style={{ width: isCustom ? '0%' : `${percentage}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              {isLoading ? (
                <span className="opacity-30">Loading…</span>
              ) : (
                <span>
                  <span className="font-medium text-foreground tabular-nums">
                    {used.toLocaleString()}
                  </span>{' '}
                  used
                </span>
              )}
              {isCustom ? (
                <span className="text-muted-foreground">Unlimited</span>
              ) : isLoading ? (
                <span className="opacity-30">—</span>
              ) : (
                <span>
                  {remaining != null && (
                    <>
                      <span className="font-medium text-foreground tabular-nums">
                        {remaining.toLocaleString()}
                      </span>{' '}
                      remaining /{' '}
                    </>
                  )}
                  <span className="tabular-nums">{limit.toLocaleString()}</span>
                </span>
              )}
            </div>
          </div>

          {/* Percentage callout */}
          {!isLoading && !isCustom && (
            <p className="text-xs text-muted-foreground">
              <span
                className={cn(
                  'font-semibold',
                  atLimit && 'text-destructive',
                  nearLimit && 'text-yellow-500',
                  !atLimit && !nearLimit && 'text-foreground',
                )}
              >
                {percentage.toFixed(1)}%
              </span>{' '}
              of your {planLabel} plan limit used.
            </p>
          )}

          {/* Upgrade CTA */}
          {(atLimit || nearLimit) && plan !== 'enterprise' && (
            <Link to="/subscription" className="w-full">
              <Button size="sm" variant={atLimit ? 'default' : 'outline'} className="w-full">
                <TrendingUp className="h-4 w-4 mr-1.5" />
                Upgrade plan
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>

      {/* Plan summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Plan limits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-border text-xs">
            {[
              {
                label: 'Executions / cycle',
                value: isCustom ? 'Unlimited' : limit.toLocaleString(),
              },
              { label: 'Current plan', value: planLabel },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between py-2">
                <span className="text-muted-foreground">{label}</span>
                <span className={cn('font-medium tabular-nums', isLoading && 'opacity-30')}>
                  {isLoading ? '—' : value}
                </span>
              </div>
            ))}
          </div>
          {plan !== 'enterprise' && (
            <p className="text-xs text-muted-foreground mt-3">
              Need higher limits?{' '}
              <Link
                to="/subscription"
                className="underline underline-offset-2 hover:text-foreground transition-colors"
              >
                View plans
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
